import { Card } from "@devhop/ui";
import { memo } from "react";
import type { NodeProps } from "reactflow";
import { Handle, Position } from "reactflow";

interface FlowStepData {
  step: {
    id: string;
    title: string;
    description: string | null;
    stepNumber: number;
    stepTypeName: string | null;
    positionName: string | null;
    departmentName: string | null;
  };
}

// Get color based on step type
function getStepTypeColor(stepTypeName: string | null): string {
  if (!stepTypeName) {
    return "bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900 dark:border-gray-800 dark:text-gray-200";
  }

  const type = stepTypeName.toLowerCase();
  if (type.includes("ວຽກ") || type.includes("work")) {
    return "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900 dark:border-blue-800 dark:text-blue-200";
  }
  if (type.includes("ກວດສອບ") || type.includes("check")) {
    return "bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:border-yellow-800 dark:text-yellow-200";
  }
  if (type.includes("ອະນຸມັດ") || type.includes("approve")) {
    return "bg-green-50 border-green-200 text-green-800 dark:bg-green-900 dark:border-green-800 dark:text-green-200";
  }
  return "bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900 dark:border-gray-800 dark:text-gray-200";
}

export const FlowStepNode = memo(({ data }: NodeProps<FlowStepData>) => {
  const { step } = data;
  const typeColor = getStepTypeColor(step.stepTypeName);

  return (
    <div className="relative">
      {/* Connection handles - All sides */}
      {/* Left: Target (incoming) */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="h-2 w-2 border border-white bg-gray-400"
      />

      {/* Top: Target (incoming) */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="h-2 w-2 border border-white bg-gray-400"
      />

      <Card
        className={`w-48 cursor-pointer gap-0 border p-3 transition-colors hover:shadow-md ${typeColor}`}
      >
        {/* Step Number Badge */}
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold text-muted-foreground text-xs">
            #{step.stepNumber}
          </span>
          {step.stepTypeName && (
            <span className="font-medium text-xs">{step.stepTypeName}</span>
          )}
        </div>

        {/* Step Title */}
        <h3 className="mb-2 font-semibold text-sm leading-tight">
          {step.title}
        </h3>

        {/* Description (truncated) */}
        {step.description && (
          <p className="mb-2 line-clamp-2 text-muted-foreground text-xs">
            {step.description}
          </p>
        )}

        {/* Responsible Position */}
        {step.positionName && (
          <div className="mb-1 text-xs">
            <span className="font-medium">ຜູ້ຮັບຜິດຊອບ: </span>
            <span className="text-muted-foreground">{step.positionName}</span>
          </div>
        )}

        {/* Department */}
        {step.departmentName && (
          <div className="text-xs">
            <span className="font-medium">ພະແນກ: </span>
            <span className="text-muted-foreground">{step.departmentName}</span>
          </div>
        )}
      </Card>

      {/* Right: Source (outgoing) */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="h-2 w-2 border border-white bg-gray-400"
      />

      {/* Bottom: Source (outgoing) */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="h-2 w-2 border border-white bg-gray-400"
      />
    </div>
  );
});

FlowStepNode.displayName = "FlowStepNode";
